import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormikValidationResult } from 'types';

import { useMobile } from '../../hooks';
import { loginFormFieldNames } from '../../shared/constants';
import FormError from '../FormError';
import Input from '../Input';
import LinkButton from '../LinkButton';
import MessageView from '../../../loginSide/components/MessageView';
import styles from './styles.module.scss';
import SubmitButton from '../SubmitButton';

interface LoginFormProps {
    errors: Record<string, FormikValidationResult>;
    formError?: React.ReactElement | string;
    isSubmitting: boolean;
    message: string | null;
    onSubmit: (event: React.SubmitEvent<HTMLFormElement>) => Promise<void>;
    switchToForgetPassword: () => void;
}

const LoginForm = forwardRef<HTMLInputElement, LoginFormProps>(
    ({ errors, formError, isSubmitting, message, onSubmit, switchToForgetPassword }, ref) => {
        const { t } = useTranslation();
        const { t: tMessages } = useTranslation('messages');
        const isError = !!formError || !!Object.keys(errors).length;
        const isMobile = useMobile();

        return (
            <form
                onSubmit={e => {
                    void onSubmit(e);
                }}
                test-id='login-form'
            >
                {message && <MessageView>{tMessages(message)}</MessageView>}
                {isError && (
                    <FormError test-id='login-form-error'>
                        {formError || errors[loginFormFieldNames.login] || errors[loginFormFieldNames.password]}
                    </FormError>
                )}
                <Input
                    autoFocus
                    error={!!errors[loginFormFieldNames.login] || !!formError}
                    name={loginFormFieldNames.login}
                    label={t('Email')}
                    test-id='login-field-email'
                    placeholder={t('myEmailIs')}
                />
                <LinkButton
                    className={styles.link}
                    onClick={switchToForgetPassword}
                    test-id='login-button-forgot-password'
                    da-id='login-button-forgot-password'
                >
                    {t('Forgot Password')}?
                </LinkButton>
                <Input
                    ref={ref}
                    type='password'
                    error={!!errors[loginFormFieldNames.password] || !!formError}
                    name={loginFormFieldNames.password}
                    label={t('Password')}
                    test-id='login-field-password'
                    placeholder={t('Enter your password')}
                />
                {isMobile ? (
                    <>
                        <SubmitButton
                            className={styles.mobile}
                            da-id='login-submit'
                            data-view='mobile'
                            progress={isSubmitting}
                            test-id='login-submit'
                        >
                            {t('Log In to Mobile Version')}
                        </SubmitButton>
                        <SubmitButton
                            da-id='login-submit'
                            progress={isSubmitting}
                            test-id='login-submit'
                            version='outlined'
                        >
                            {t('Log In to Desktop Version')}
                        </SubmitButton>
                    </>
                ) : (
                    <SubmitButton
                        da-id='login-submit'
                        progress={isSubmitting}
                        test-id='login-submit'
                        version='contained'
                    >
                        {t('Log In')}
                    </SubmitButton>
                )}
            </form>
        );
    },
);

export default LoginForm;
